import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { CheckCircle2, CircleDashed, ChevronRight } from "lucide-react";
import { apiClient } from "@/lib/apiClient";

const fmt = (v) => new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF", minimumFractionDigits: 2 }).format(v || 0);

const TONE = {
  green: { card: "border-emerald-200 bg-emerald-50/60", dot: "bg-emerald-500", text: "text-emerald-800", label: "Alles zugeordnet" },
  amber: { card: "border-amber-200 bg-amber-50/60", dot: "bg-amber-500", text: "text-amber-800", label: "Offen" },
  red: { card: "border-rose-200 bg-rose-50/60", dot: "bg-rose-500", text: "text-rose-800", label: "Überfällig (> 30 Tage)" },
};

export default function ClearingStatusCard({ companyId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    if (!companyId) return;
    apiClient.get(`/companies/${companyId}/bank/clearing-status`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const tone = TONE[data.status] || TONE.amber;
  const open = data.count > 0;

  return (
    <Card
      className={`p-5 border cursor-pointer transition-colors hover:shadow-sm ${tone.card}`}
      onClick={() => navigate("/bank-reconciliation")}
      role="button"
      data-testid="dashboard-clearing-card"
      data-status={data.status}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className={`h-2.5 w-2.5 rounded-full ${tone.dot}`} data-testid="clearing-status-dot" />
          <h3 className="text-sm font-semibold text-slate-900">Durchlaufkonto 1099 · Unklare Beträge</h3>
        </div>
        <span className={`text-[11px] font-medium ${tone.text}`} data-testid="clearing-status-label">{tone.label}</span>
      </div>
      <div className="mt-3 flex items-end justify-between gap-4">
        <div>
          <div className="text-2xl font-bold text-slate-900" data-testid="clearing-balance">{fmt(data.balance)}</div>
          <div className="mt-0.5 text-xs text-slate-600" data-testid="clearing-count">
            {open
              ? `${data.count} offene Bankbewegung${data.count === 1 ? "" : "en"} · älteste vom ${new Date(data.oldest_date).toLocaleDateString("de-CH")}`
              : "Keine offenen Bankbewegungen – Saldenliste ausgeglichen"}
          </div>
        </div>
        {open
          ? <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-700"><CircleDashed className="h-3.5 w-3.5" /> Zuordnen <ChevronRight className="h-3.5 w-3.5" /></span>
          : <CheckCircle2 className="h-6 w-6 text-emerald-600" />}
      </div>
    </Card>
  );
}
